import React, { useCallback, useRef, useState } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import scrollIntoView from 'scroll-into-view-if-needed';
import { nanoid } from 'nanoid';
import isEqual from 'lodash/isEqual';

import keys from '../../utils/keys';
import { transitionComplete } from '../../utils/timedEvents';
import useClickOutside from '../../react-hooks/use-click-outside';

const CLEAR_TIME = 500;

function Dropdown({
  disabled,
  hiddenLabel,
  id,
  initialValue = '',
  label,
  onSelect,
  options,
  visibleLabel
}) {
  const hiddenLabelId = useRef(`id-${nanoid()}`);
  const visibleLabelId = useRef(`id-${nanoid()}`);
  const initialSelectedOption =
    options.find((option) => option.value === initialValue) || null;
  const buttonRef = useRef(null);
  const containerRef = useRef(null);
  const listboxRef = useRef(null);
  const [keysSoFar, setKeysSoFar] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(initialSelectedOption);
  const itemRef = useCallback((node) => {
    if (node) {
      scrollIntoView(node, {
        behavior: 'smooth',
        scrollMode: 'if-needed',
        block: 'nearest'
      });
    }
  }, []);

  useClickOutside(containerRef, () => setIsOpen(false));

  function clearKeysSoFar() {
    setTimeout(() => setKeysSoFar(''), CLEAR_TIME);
  }

  function selectOption(option) {
    onSelect(option.value);
    setSelectedOption(option);
  }

  function openListbox() {
    setIsOpen(true);
    if (!selectedOption) {
      setSelectedOption(options[0]);
    }
  }

  function onButtonClick() {
    if (!isOpen) {
      openListbox();
      transitionComplete(() => listboxRef.current.focus());
    } else {
      setIsOpen(false);
    }
  }

  function searchOptionToFocus() {
    const indexToFocus = options.findIndex((option) =>
      option.label.toLowerCase().startsWith(keysSoFar)
    );

    if (indexToFocus >= 0) {
      setSelectedOption(options[indexToFocus]);
    }
  }

  function handleKeyDown(e) {
    e.preventDefault();
    const { length } = options;
    const selectedOptionIndex = options.findIndex(
      (option) => option.value === selectedOption.value
    );

    switch (e.keyCode) {
      case keys.enter:
      case keys.space:
        selectOption(selectedOption);
        setIsOpen(false);
        buttonRef.current.focus();
        break;
      case keys.escape:
        setIsOpen(false);
        buttonRef.current.focus();
        break;
      case keys.home:
        selectOption(options[0]);
        break;
      case keys.end:
        selectOption(options[options.length - 1]);
        break;
      case keys.down:
        selectOption(options[(selectedOptionIndex + 1) % length]);
        break;
      case keys.up:
        selectOption(options[(selectedOptionIndex - 1 + length) % length]);
        break;
      default:
        setKeysSoFar(`${keysSoFar}${e.key}`);
        if (keysSoFar) {
          searchOptionToFocus();
        }
        clearKeysSoFar();
        break;
    }
  }

  return (
    <div className="dropdown" ref={containerRef}>
      {hiddenLabel && (
        <span
          id={hiddenLabelId.current}
          className="dropdown__hidden-label visually-hidden"
        >
          {hiddenLabel}
        </span>
      )}
      {visibleLabel && (
        <label
          className="dropdown__label"
          id={visibleLabelId.current}
          htmlFor={id}
        >
          {visibleLabel}
        </label>
      )}
      <div
        className={`dropdown__wrapper ${
          isOpen ? 'dropdown__wrapper--open' : ''
        }`}
      >
        <button
          ref={buttonRef}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-labelledby={
            visibleLabel ? visibleLabelId.current : hiddenLabelId.current
          }
          id={id}
          className="dropdown__button"
          disabled={disabled}
          onClick={onButtonClick}
        >
          <span className="dropdown__selected-option-label">
            {selectedOption?.label || label || options[0]?.label}
          </span>
          <svg className="dropdown__icon">
            <use xlinkHref="#chevron-down-outline" />
          </svg>
        </button>
        <ul
          onKeyDown={(e) => handleKeyDown(e)}
          ref={listboxRef}
          aria-labelledby={
            visibleLabel ? visibleLabelId.current : hiddenLabelId.current
          }
          tabIndex="-1"
          role="listbox"
          className="dropdown__listbox"
          {...(selectedOption && {
            'aria-activedescendant': selectedOption.value
          })}
        >
          {options.map((option) => (
            <li
              key={option.value}
              aria-label={`${hiddenLabel || visibleLabel} ${option.label}`}
              aria-selected={isEqual(option, selectedOption)}
              id={option.value}
              className="dropdown__option"
              role="option"
              onClick={() => {
                selectOption(option);
                setIsOpen(false);
              }}
              {...(selectedOption === option && { ref: itemRef })}
            >
              {option.label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

Dropdown.propTypes = {
  disabled: bool,
  hiddenLabel: string,
  id: string.isRequired,
  initialValue: string,
  label: string,
  onSelect: func.isRequired,
  options: arrayOf(
    shape({
      label: string.isRequired,
      value: string.isRequired
    }).isRequired
  ).isRequired,
  visibleLabel: string.isRequired
};

export default React.memo(Dropdown);
